import { Dropdown } from 'bootstrap-5'

/**
 * Bootstrap 5 dropdown toggle driven only by the app's bootstrap-5 copy.
 *
 * chameleon-vue bundles its own (beta) Bootstrap 5, whose data-api listens on the
 * document in the capture phase. With both copies handling data-bs-toggle="dropdown",
 * one click opens and closes the menu again. Toggles using this directive must NOT
 * have data-bs-toggle="dropdown"; this handles toggling, closing other menus, outside
 * clicks (honouring data-bs-auto-close) and escape. Other data-bs-* options such as
 * data-bs-auto-close and data-bs-popper-config are still read by Bootstrap.
 *
 * Usage: <button type="button" v-dropdown data-bs-auto-close="outside">
 */
const SELECTOR = '[data-v-dropdown]'

const hideOthers = (el) => {
  document.querySelectorAll(`${SELECTOR}.show`).forEach((toggle) => {
    const other = Dropdown.getInstance(toggle)

    if (toggle !== el && other) {
      other.hide()
    }
  })
}

export default {
  bind (el) {
    el.setAttribute('data-v-dropdown', '')

    const onClick = (event) => {
      event.preventDefault()
      hideOthers(el)
      Dropdown.getOrCreateInstance(el).toggle()
    }

    const onDocumentClick = (event) => {
      const instance = Dropdown.getInstance(el)

      if (!instance || !el.classList.contains('show')) {
        return
      }

      // composedPath survives elements being removed during the click (e.g. multiselect options)
      const path = event.composedPath()

      if (path.includes(el)) {
        return
      }

      const autoClose = instance._config.autoClose
      const insideMenu = path.includes(instance._menu)
      const isFormField = /input|select|option|textarea|form/i.test(event.target.tagName)

      if (insideMenu && (autoClose === 'outside' || autoClose === false || isFormField)) {
        return
      }

      if (!insideMenu && (autoClose === 'inside' || autoClose === false)) {
        return
      }

      instance.hide()
    }

    // Listen on window in the capture phase so this runs before either Bootstrap copy's
    // document keydown handler, which would otherwise error looking for a data-bs-toggle
    const onKeydown = (event) => {
      const instance = Dropdown.getInstance(el)

      if (!instance || !el.classList.contains('show') || !['Escape', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
        return
      }

      const path = event.composedPath()
      const fromDropdown = path.includes(el) || path.includes(instance._menu)

      if (fromDropdown) {
        event.stopPropagation()
      }

      // Escape closes the open menu wherever focus is (e.g. back on body after a multiselect pick)
      if (event.key === 'Escape') {
        instance.hide()

        if (fromDropdown) {
          el.focus()
        }
      }
    }

    el.addEventListener('click', onClick)
    document.addEventListener('click', onDocumentClick)
    window.addEventListener('keydown', onKeydown, true)

    el._vDropdown = { onClick, onDocumentClick, onKeydown }
  },

  unbind (el) {
    const handlers = el._vDropdown

    if (handlers) {
      el.removeEventListener('click', handlers.onClick)
      document.removeEventListener('click', handlers.onDocumentClick)
      window.removeEventListener('keydown', handlers.onKeydown, true)
      delete el._vDropdown
    }

    const instance = Dropdown.getInstance(el)

    if (instance) {
      instance.dispose()
    }
  }
}
